import {
  formatDateDisplay,
  NAME_TOOLTIP_TEXT,
  normalizeFirstName,
  normalizeLastName,
  useAppForm,
} from '@massilia/features'
import { type GetMyProfileResponse, toError, useUpdateMyProfileMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useState } from 'react'
import { toast } from 'sonner'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import {
  updateCompanyProfileSchema,
  updateProfileSchema,
} from '@/lib/schemas/personal-informations/personal-informations.schemas'
import { useAuthStore } from '@/stores/auth.store'
import { GENDER_LABELS_FULL } from '@/utils/gender-labels'

import { DisplayRow } from './display-row'
import { EditableSection } from './editable-section'

interface PersonalDataSectionProps {
  profile: GetMyProfileResponse
}

const GENDER_OPTIONS = [
  { label: 'Homme', value: 'MALE' },
  { label: 'Femme', value: 'FEMALE' },
] as const

export function PersonalDataSection({ profile }: PersonalDataSectionProps) {
  return profile.client?.clientType === 'COMPANY' ? (
    <CompanyDataSection profile={profile} />
  ) : (
    <IndividualDataSection profile={profile} />
  )
}

function CompanyDataSection({ profile }: PersonalDataSectionProps) {
  const [isEditing, setIsEditing] = useState(false)
  const { mutateAsync: updateProfile } = useUpdateMyProfileMutation()
  const { updateAuth, user } = useAuthStore()

  const form = useAppForm({
    defaultValues: {
      companyName: profile.client?.companyName ?? '',
      address: profile.client?.address ?? '',
      city: profile.client?.city ?? '',
      postalCode: profile.client?.postalCode ?? '',
    },
    onSubmit: async ({ value }) => {
      try {
        await updateProfile({
          companyName: value.companyName,
          address: value.address,
          city: value.city,
          postalCode: value.postalCode,
        })
        // La raison sociale est aussi le nom d'affichage de l'espace client (User.firstName).
        if (user) {
          updateAuth({
            isAuthenticated: true,
            user: { ...user, firstName: value.companyName, lastName: '' },
          })
        }
        setIsEditing(false)
      } catch (error) {
        toast.error(resolveAuthErrorMessage(toError(error)))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate(
          { validationSource: 'form', value },
          updateCompanyProfileSchema
        ),
    },
  })

  const handleCancel = () => {
    form.reset()
    setIsEditing(false)
  }

  const referentName = [profile.client?.firstName, profile.client?.lastName]
    .filter(Boolean)
    .join(' ')

  return (
    <EditableSection
      actionLabel="Modifier les données"
      isEditing={isEditing}
      onToggleEdit={() => (isEditing ? handleCancel() : setIsEditing(true))}
      editForm={
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          <form.AppField
            name="companyName"
            children={(field) => <field.TextField label="Raison sociale" />}
          />
          <form.AppField
            name="address"
            children={(field) => <field.TextField label="Adresse" optional />}
          />
          <form.AppField
            name="postalCode"
            children={(field) => <field.TextField label="Code postal" optional />}
          />
          <form.AppField
            name="city"
            children={(field) => <field.TextField label="Ville" optional />}
          />
          <div className="flex gap-3">
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <>
                  <Button
                    className="bg-brand-600 text-white hover:bg-brand-700"
                    disabled={isSubmitting}
                    type="submit"
                  >
                    {isSubmitting ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    onClick={handleCancel}
                    type="button"
                    variant="outline"
                  >
                    Annuler
                  </Button>
                </>
              )}
            </form.Subscribe>
          </div>
        </form>
      }
    >
      <div className="flex flex-col">
        <DisplayRow label="Raison sociale" noTranslate value={profile.client?.companyName} />
        <DisplayRow label="SIRET" value={profile.client?.siret} />
        <DisplayRow label="Contact référent" noTranslate value={referentName} />
        <DisplayRow label="Adresse" value={profile.client?.address} />
        <DisplayRow label="Code postal" value={profile.client?.postalCode} />
        <DisplayRow label="Ville" value={profile.client?.city} />
      </div>
    </EditableSection>
  )
}

function formatDate(iso: null | string | undefined): string {
  if (!iso) return ''
  return formatDateDisplay(iso)
}

function IndividualDataSection({ profile }: PersonalDataSectionProps) {
  const [isEditing, setIsEditing] = useState(false)
  const { mutateAsync: updateProfile } = useUpdateMyProfileMutation()
  const { updateAuth, user } = useAuthStore()

  const form = useAppForm({
    defaultValues: {
      firstName: normalizeFirstName(profile.user.firstName),
      lastName: normalizeLastName(profile.user.lastName),
      birthDate: profile.client?.birthDate ?? null,
      gender: profile.client?.gender ?? null,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateProfile({
          firstName: value.firstName,
          lastName: value.lastName,
          birthDate: value.birthDate,
          gender: value.gender,
        })
        if (user) {
          updateAuth({
            isAuthenticated: true,
            user: { ...user, firstName: value.firstName, lastName: value.lastName },
          })
        }
        setIsEditing(false)
      } catch (error) {
        toast.error(resolveAuthErrorMessage(toError(error)))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, updateProfileSchema),
    },
  })

  const handleCancel = () => {
    form.reset()
    setIsEditing(false)
  }

  return (
    <EditableSection
      actionLabel="Modifier les données"
      isEditing={isEditing}
      onToggleEdit={() => (isEditing ? handleCancel() : setIsEditing(true))}
      editForm={
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          <form.AppField
            name="gender"
            children={(field) => (
              <field.RadioGroupField
                label="Sexe"
                options={GENDER_OPTIONS}
                orientation="horizontal"
                optional
              />
            )}
          />
          <form.AppField
            name="firstName"
            children={(field) => (
              <field.TextField label="Prénoms" questionToolTip={NAME_TOOLTIP_TEXT} />
            )}
          />
          <form.AppField
            name="lastName"
            children={(field) => (
              <field.TextField label="Nom de famille" questionToolTip={NAME_TOOLTIP_TEXT} />
            )}
          />
          <form.AppField
            name="birthDate"
            children={(field) => <field.DateField label="Date de naissance" optional />}
          />
          <div className="flex gap-3">
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <>
                  <Button
                    className="bg-brand-600 text-white hover:bg-brand-700"
                    disabled={isSubmitting}
                    type="submit"
                  >
                    {isSubmitting ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    onClick={handleCancel}
                    type="button"
                    variant="outline"
                  >
                    Annuler
                  </Button>
                </>
              )}
            </form.Subscribe>
          </div>
        </form>
      }
    >
      <div className="flex flex-col">
        <DisplayRow
          label="Sexe"
          value={profile.client?.gender ? GENDER_LABELS_FULL.get(profile.client.gender) : undefined}
        />
        <DisplayRow
          label="Prénoms"
          noTranslate
          value={normalizeFirstName(profile.user.firstName)}
        />
        <DisplayRow
          label="Nom de famille"
          noTranslate
          value={normalizeLastName(profile.user.lastName)}
        />
        <DisplayRow label="Date de naissance" value={formatDate(profile.client?.birthDate)} />
      </div>
    </EditableSection>
  )
}
